import { NgIf } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
    ConnectivityBannerKind,
    ConnectivityService,
    ConnectivitySnapshot,
} from 'shared/services/connectivity.service';

@Component({
    selector: 'app-connection-status',
    templateUrl: './connection-status.component.html',
    styleUrls: ['./connection-status.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgIf],
})
export class ConnectionStatusComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('banner') private banner?: ElementRef<HTMLElement>;

    snapshot: ConnectivitySnapshot = { banner: null, checking: false };
    kind: ConnectivityBannerKind | null = null;
    label = '';

    private stateSub?: Subscription;
    private resizeObserver?: ResizeObserver;
    private clearTimer = 0;

    constructor(
        private connectivity: ConnectivityService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        document.getElementById('boot-connectivity-banner')?.remove();
        this.connectivity.start();
        this.stateSub = this.connectivity.state$.subscribe((snapshot) => {
            this.snapshot = snapshot;
            if (snapshot.banner) {
                window.clearTimeout(this.clearTimer);
                this.kind = snapshot.banner;
                this.label = this.messageFor(snapshot.banner);
            } else {
                window.clearTimeout(this.clearTimer);
                this.clearTimer = window.setTimeout(() => {
                    if (!this.snapshot.banner) {
                        this.kind = null;
                        this.label = '';
                        this.changeDetectorRef.markForCheck();
                    }
                }, 240);
            }
            this.changeDetectorRef.markForCheck();
            this.syncChrome();
        });
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver !== 'undefined' && this.banner) {
            this.resizeObserver = new ResizeObserver(() => this.syncChrome());
            this.resizeObserver.observe(this.banner.nativeElement);
        }

        this.syncChrome();
    }

    ngOnDestroy(): void {
        this.stateSub?.unsubscribe();
        this.resizeObserver?.disconnect();
        window.clearTimeout(this.clearTimer);

        document.body.classList.remove('connectivity-banner-visible');
        document.documentElement.style.setProperty('--connectivity-banner-offset', '0px');
    }

    retry(): void {
        this.connectivity.retry();
    }

    messageFor(kind: ConnectivityBannerKind | null): string {
        switch (kind) {
            case 'offline':
                return "You're offline. Check your internet connection.";
            case 'server':
                return "Can't reach the server. We'll keep trying.";
            case 'restored':
                return "You're back online.";
            default:
                return '';
        }
    }

    private syncChrome(): void {
        const visible = !!this.snapshot.banner;
        const root = document.documentElement;

        if (!visible) {
            root.style.setProperty('--connectivity-banner-offset', '0px');
            document.body.classList.remove('connectivity-banner-visible');
            return;
        }

        document.body.classList.add('connectivity-banner-visible');
        root.style.setProperty('--connectivity-banner-offset', '32px');
    }
}
